/**
 * Shared status styling utilities for consistent styling across fleet and downtime components
 */

/**
 * Get status-specific styling for cards and components
 * Mock style: tinted surfaces with colored borders; solid fill when selected.
 * @param {string} status - The status type
 * @param {boolean} isActive - Whether the status is currently active/selected
 * @returns {Object} Status styling object with border, background, and color classes
 */
export const getStatusStyle = (status, isActive = false) => {
  // Safety check for status parameter
  if (!status || typeof status !== "string") {
    status = "all";
  }

  const statusStyleMap = {
    // Fleet status — same accents as status pills (status-utils STATUS_PILL)
    normal: {
      border: "border-asset-running dark:border-asset-running",
      activeBg: "bg-asset-running dark:bg-asset-running",
      inactiveBg: "bg-asset-running/10 dark:bg-asset-running/15",
      iconColor: "text-asset-running dark:text-green-400",
      valueColor: "text-foreground",
      labelColor: "text-green-700 dark:text-green-400",
    },
    offline: {
      border: "border-destructive",
      activeBg: "bg-destructive",
      inactiveBg: "bg-destructive/10 dark:bg-destructive/15",
      iconColor: "text-destructive",
      valueColor: "text-foreground",
      labelColor: "text-destructive dark:text-red-300",
    },
    danger: {
      border: "border-orange-500 dark:border-orange-400",
      activeBg: "bg-orange-500 dark:bg-orange-500",
      inactiveBg: "bg-orange-50 dark:bg-orange-500/15",
      iconColor: "text-orange-500 dark:text-orange-400",
      valueColor: "text-foreground",
      labelColor: "text-orange-700 dark:text-orange-300",
    },
    alerted: {
      border: "border-asset-warning dark:border-asset-warning",
      activeBg: "bg-asset-warning dark:bg-asset-warning",
      inactiveBg: "bg-asset-warning/10 dark:bg-asset-warning/15",
      iconColor: "text-asset-warning dark:text-amber-400",
      valueColor: "text-foreground",
      labelColor: "text-amber-800 dark:text-amber-300",
    },
    nodata: {
      border: "border-slate-400 dark:border-slate-400",
      activeBg: "bg-slate-500 dark:bg-slate-500",
      inactiveBg: "bg-slate-100 dark:bg-slate-500/15",
      iconColor: "text-slate-500 dark:text-slate-400",
      valueColor: "text-foreground",
      labelColor: "text-slate-600 dark:text-slate-300",
    },
    standby: {
      border: "border-slate-400 dark:border-slate-500",
      activeBg: "bg-slate-500 dark:bg-slate-500",
      inactiveBg: "bg-slate-100 dark:bg-slate-400/15",
      iconColor: "text-slate-500 dark:text-slate-400",
      valueColor: "text-foreground",
      labelColor: "text-slate-600 dark:text-slate-300",
    },
    unknown: {
      border: "border-slate-300 dark:border-slate-500",
      activeBg: "bg-slate-400 dark:bg-slate-500",
      inactiveBg: "bg-slate-100 dark:bg-slate-500/15",
      iconColor: "text-slate-400 dark:text-slate-500",
      valueColor: "text-foreground",
      labelColor: "text-slate-600 dark:text-slate-400",
    },
    // Downtime KPI — pastel fills + bold accent borders (light mock)
    downtime: {
      border: "border-2 border-[#D4B06A] dark:border-orange-400/50",
      activeBg: "bg-orange-500 dark:bg-orange-500",
      inactiveBg: "bg-[#F5EFE3] dark:bg-orange-500/15",
      iconColor: "text-amber-600 dark:text-orange-400",
      valueColor: "text-foreground",
      labelColor: "text-amber-600 dark:text-orange-400",
    },
    mechanical: {
      border: "border-2 border-[#C78287] dark:border-destructive/60",
      activeBg: "bg-destructive",
      inactiveBg: "bg-[#F2E6E8] dark:bg-destructive/15",
      iconColor: "text-destructive",
      valueColor: "text-foreground",
      labelColor: "text-destructive",
    },
    events: {
      border: "border-2 border-[#D4B06A] dark:border-amber-400/50",
      activeBg: "bg-amber-500 dark:bg-amber-500",
      inactiveBg: "bg-[#F5EFE3] dark:bg-amber-400/15",
      iconColor: "text-amber-600 dark:text-amber-400",
      valueColor: "text-foreground",
      labelColor: "text-amber-600 dark:text-amber-400",
    },
    // Soft cyan summary (Total Downtime / Total Silent)
    summary: {
      border: "border-2 border-[#5FB8C8] dark:border-brand-cyan/60",
      activeBg: "bg-brand-cyan",
      inactiveBg: "bg-[#DDF0F6] dark:bg-brand-cyan/15",
      iconColor: "text-brand-cyan",
      valueColor: "text-foreground",
      labelColor: "text-brand-cyan",
    },
    // Total Fleet — Signal Cyan when selected; soft cyan when not
    all: {
      border: "border-brand-cyan",
      activeBg: "bg-brand-cyan",
      inactiveBg: "bg-[#DDF0F6] dark:bg-brand-cyan/15",
      iconColor: "text-brand-cyan",
      valueColor: "text-foreground",
      labelColor: "text-brand-cyan",
    },
  };

  const statusStyle = statusStyleMap[status] || statusStyleMap.all;

  return {
    ...statusStyle,
    cardClass: `border-2 rounded-md shadow-sm transition-all duration-200 ${statusStyle.border} ${
      isActive ? statusStyle.activeBg : statusStyle.inactiveBg
    }`,
  };
};

/**
 * Common card styling classes
 */
export const cardStyles = {
  /** Fleet-overview style panel shell */
  panel: "bg-card border border-border shadow-sm rounded-lg overflow-hidden",
  header:
    "flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 rounded-t-lg border-b border-border",
  content: "p-0",
  /**
   * Scrollable table body. Right border on the table keeps a continuous edge
   * against the scrollbar so zebra (muted) rows do not form a jagged
   * pseudo-border while card-colored rows bleed into the track.
   */
  tableContainer:
    "overflow-x-auto overflow-y-auto bg-card max-h-[min(70vh,52rem)] [&>table]:border-r [&>table]:border-border",
  /**
   * Viewport-fill table region — grows into leftover space, floors height so
   * the page can scroll when chrome above the table leaves little room.
   */
  tableFillRegion: "flex flex-1 flex-col min-h-[min(70vh,40rem)]",
  /** Scroll body inside a fill-height table card (overrides capped max-height) */
  tableFillBody: "flex-1 min-h-0 max-h-none",
  /** Main page content under the header (scrollable; modest bottom padding) */
  pageContent:
    "flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 pb-6 sm:pb-8 flex flex-col",
  /** Spacer below fill-height tables so bottom gap stays visible when scrolling */
  pageBottomSpacer: "h-4 sm:h-6 shrink-0",
  tableHeader: "bg-card",
  tableRow: "border-b border-border",
  /** Body rows — no zebra striping (flat card background), hover works in both modes. */
  bodyRow:
    "border-b border-border bg-card hover:bg-muted dark:hover:bg-white/10 transition-colors",
  tableHead:
    "cursor-pointer hover:bg-muted/80 select-none py-3 px-4 text-left text-sm font-medium text-muted-foreground border-b border-border sticky top-0 bg-card z-10",
  tableCell: "py-3 px-4 text-left text-sm font-normal text-foreground",
};

/**
 * Outline chrome surface (grey border, card fill).
 * Pair with Button size variants, or {@link mapPopupActionButton}.
 */
export const outlineChromeSurface =
  "font-normal text-sm border border-border bg-background dark:bg-card text-foreground hover:bg-muted dark:hover:bg-muted shadow-none [&_svg]:text-muted-foreground";

/** Outline chrome buttons (Filters / Export / Columns / downtime View) */
export const outlineChromeButton = `h-9 gap-1.5 ${outlineChromeSurface}`;

/**
 * Shared steel surface — light: sidebar-accent; dark: segmented-selected.
 * Used by filled chrome, count bubbles, and selected list rows.
 */
export const steelFill =
  "bg-sidebar-accent text-sidebar-accent-foreground dark:bg-segmented-selected dark:text-foreground";

/** Hover wash for steel-filled controls */
export const steelFillHover =
  "hover:bg-sidebar-accent/80 dark:hover:bg-segmented-selected/90";

/**
 * Count bubble in filter lists / combobox options.
 * Dark fill stays visible on muted rows (not hover-only).
 */
export const countBubble = `shrink-0 border-transparent tabular-nums font-normal ${steelFill}`;

/** Filled steel chrome (Copy Coords, Back, Add, trend actions, sort selected) */
export const steelChromeButton =
  `font-normal text-sm shadow-none transition-colors border border-border ${steelFill} ${steelFillHover} hover:text-sidebar-accent-foreground dark:border-segmented-selected [&_svg]:text-sidebar-accent-foreground`;

/** Equal-size map popup footer actions (View / Copy Coords) */
export const mapPopupActionButton = "flex-1 px-4 py-2 rounded cursor-pointer";

/** Idle companion for steel chrome toggles (sort Drift / A–Z, etc.) */
export const steelChromeButtonIdle =
  "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground dark:bg-card";

/** Selected table / list row wash (overrides zebra) */
export const steelRowSelected =
  `${steelFill} even:bg-sidebar-accent ${steelFillHover} dark:even:bg-segmented-selected`;

/** Small icon outline buttons in table toolbars */
export const outlineIconButton =
  "p-1 h-6 w-6 flex items-center justify-center border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors rounded shadow-none disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent";

/** Info / help tooltip icon (header hints, etc.) — Signal Cyan */
export const infoTooltipIcon = "text-brand-cyan";

/** Selected surface for segmented tabs / date pills (white light / steel dark) */
const segmentedSelectedSurface =
  "bg-segmented-selected text-sidebar-accent-foreground shadow-sm";

/**
 * Segmented tab track (grey well). Pair with selected/idle — no left rail.
 */
export const segmentedTabTrack =
  "inline-flex items-center gap-1 rounded-md bg-sidebar-accent p-1";

/** Selected segmented tab pill */
export const segmentedTabSelected = `rounded-md ${segmentedSelectedSurface}`;

/** Idle segmented tab */
export const segmentedTabIdle =
  "rounded-md text-muted-foreground hover:text-foreground";

/** Selected sidebar nav row */
export const sidebarNavSelected =
  "bg-sidebar-accent text-sidebar-accent-foreground";

/** 2px steel accent rail — sidebar selected rows only */
export const sidebarNavRail =
  "absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-sidebar-nav-rail";

/**
 * Date / time-range pill track — same steel well as segmented asset tabs.
 */
export const dateRangePillTrack =
  "inline-flex max-w-full flex-wrap items-center gap-1 rounded-full bg-sidebar-accent p-1";

/** Selected date / time-range pill */
export const dateRangePillSelected =
  `border-transparent ${segmentedSelectedSurface} hover:bg-segmented-selected hover:text-sidebar-accent-foreground`;

/** Idle date / time-range pill */
export const dateRangePillIdle =
  "border-transparent bg-transparent text-muted-foreground shadow-none hover:bg-transparent hover:text-foreground";

/**
 * Common filter styling classes
 */
export const filterStyles = {
  container: "bg-card border border-border rounded-lg p-4",
  label: "text-sm font-medium text-foreground",
  clearButton:
    "text-muted-foreground hover:text-foreground hover:bg-muted px-2 py-1 h-8",
};

/**
 * Common search styling classes
 */
export const searchStyles = {
  container: "relative",
  input:
    "pr-10 w-full sm:w-64 h-9 border-border bg-background text-foreground placeholder:text-muted-foreground shadow-none",
  icon: "absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none",
};
